import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, AlertCircle, Scissors, Shirt, Clock, Package, Truck, Inbox, ExternalLink, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/logo.jpg";

const typeLabels = {
  shirt: "Shirt", pant: "Pant", trouser: "Trouser", pajama: "Pajama", suit_jacket: "Suit Jacket",
  sherwani: "Sherwani", kurta: "Kurta", short_kurta: "Short Kurta", pathani: "Pathani",
  waistcoat: "Waistcoat", modi_jacket: "Modi Jacket", blazer: "Blazer", shalwar: "Shalwar",
  police_uniform: "Police Uniform", coat: "Coat", prince_coat: "Prince Coat", custom: "Custom"
};

const statusLabels = {
  pending: "Pending",
  sent_to_worker: "Sent to Worker",
  received_from_worker: "Received from Worker",
  product_ready: "Product Ready",
  received: "Received"
};

const statusColors = {
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  sent_to_worker: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  received_from_worker: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  product_ready: "bg-green-500/20 text-green-400 border-green-500/30",
  received: "bg-purple-500/20 text-purple-400 border-purple-500/30"
};

const statusIcons = {
  pending: Clock,
  sent_to_worker: Truck,
  received_from_worker: Inbox,
  product_ready: CheckCircle,
  received: Package
};

const statusFlow = ["pending", "sent_to_worker", "received_from_worker", "product_ready", "received"];

export default function ScanPage() {
  const { slipId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const articleNum = searchParams.get("article");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("vr_token") : null;
  const isAdmin = !!token;

  const loadScan = useCallback(() => {
    axios.get(`${BACKEND_URL}/api/scan/${slipId}`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => { setError("Invalid QR code or slip not found"); setLoading(false); });
  }, [slipId]);

  useEffect(() => { loadScan(); }, [loadScan]);

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/slips/${slipId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${statusLabels[newStatus]}`);
      loadScan();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        localStorage.removeItem("vr_token");
        localStorage.removeItem("vr_admin");
        navigate("/login");
      } else {
        toast.error(detail || "Failed to update status");
      }
    }
    setUpdating(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-pulse gold-text font-serif text-xl">Scanning...</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="luxury-card rounded-sm max-w-sm w-full">
        <CardContent className="p-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400/60" />
          <h1 className="font-serif text-lg text-foreground mb-2">Scan Failed</h1>
          <p className="text-muted-foreground text-sm">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  const currentStatus = data.status || "pending";
  const currentIdx = statusFlow.indexOf(currentStatus);
  const CurrentIcon = statusIcons[currentStatus] || Clock;

  // ───────────────────────────── ADMIN: focused status-update page ─────────────────────────────
  if (isAdmin) {
    const nextStatuses = statusFlow.filter((_, i) => i > currentIdx);
    return (
      <div data-testid="scan-page" className="min-h-screen bg-background p-4 flex items-center justify-center">
        <div className="max-w-md w-full space-y-6 animate-fade-in-up">
          {/* Compact header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="VR" className="w-10 h-10 rounded-full object-cover border border-[rgba(212,175,55,0.3)]" />
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Update Status</p>
                <p className="font-serif text-lg text-foreground leading-tight">{data.slip_number}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/slips/${slipId}`)}
              data-testid="scan-open-slip-btn"
              className="text-[11px] gap-1 h-8 text-muted-foreground"
            >
              Full Slip <ExternalLink className="w-3 h-3" />
            </Button>
          </div>

          {/* Current status — big & clear */}
          <Card className="luxury-card rounded-sm border-[rgba(212,175,55,0.15)]">
            <CardContent className="p-6 text-center space-y-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Current Status</p>
              <div className="flex items-center justify-center gap-2">
                <CurrentIcon className="w-5 h-5 gold-text" />
                <span
                  data-testid="scan-status-badge"
                  className={`px-4 py-1.5 rounded-full text-sm font-medium border ${statusColors[currentStatus]}`}
                >
                  {statusLabels[currentStatus]}
                </span>
              </div>
              {articleNum && (
                <p className="text-[11px] text-muted-foreground">Scanned Article #{articleNum}</p>
              )}
            </CardContent>
          </Card>

          {/* Next-status buttons — one tap each */}
          {nextStatuses.length > 0 ? (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground text-center">Move To</p>
              {nextStatuses.map(s => {
                const Icon = statusIcons[s];
                const isImmediateNext = s === statusFlow[currentIdx + 1];
                return (
                  <button
                    key={s}
                    onClick={() => handleStatusUpdate(s)}
                    disabled={updating}
                    data-testid={`scan-status-btn-${s}`}
                    className={`w-full flex items-center justify-between gap-3 rounded-sm px-5 py-4 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                      isImmediateNext
                        ? "bg-[#722F37] hover:bg-[#8B3A44] text-white border border-[#722F37]"
                        : "bg-card hover:bg-[rgba(212,175,55,0.05)] text-foreground border border-border hover:border-[rgba(212,175,55,0.3)]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <div className="text-left">
                        <p className="text-sm font-semibold leading-tight">{statusLabels[s]}</p>
                        {isImmediateNext && (
                          <p className={`text-[10px] mt-0.5 ${isImmediateNext ? "text-white/70" : "text-muted-foreground"}`}>
                            Next step
                          </p>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" />
              <p className="text-sm text-foreground font-medium">Order Delivered</p>
              <p className="text-[11px] text-muted-foreground">This order is fully complete.</p>
            </div>
          )}

          {/* Small footer hint */}
          <p className="text-[10px] text-muted-foreground/60 text-center uppercase tracking-widest">
            Logged in as Admin
          </p>
        </div>
      </div>
    );
  }

  // ───────────────────────────── PUBLIC (unauthenticated) — read-only tracking ─────────────────────────────
  return (
    <div data-testid="scan-page" className="min-h-screen bg-background p-4 flex items-center justify-center">
      <div className="max-w-md w-full space-y-4 animate-fade-in-up">
        <div className="text-center">
          <img src={LOGO_URL} alt="VR" className="w-14 h-14 rounded-full object-cover border-2 border-[rgba(212,175,55,0.3)] mx-auto mb-3 shadow-lg" />
          <h1 className="font-serif text-xl font-semibold text-foreground">VR The Darzi Studio</h1>
          <div className="w-12 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mt-2" />
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground mt-2">Production Tracking</p>
        </div>

        <Card className="luxury-card rounded-sm overflow-hidden shadow-xl border-[rgba(212,175,55,0.15)]">
          <div className="bg-gradient-to-r from-green-500/10 to-green-500/5 border-b border-green-500/20 px-5 py-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-green-400">Scan Successful</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {data.slip_number}{articleNum ? ` · Article ${articleNum}` : ""}
              </p>
            </div>
          </div>

          <CardContent className="p-5 space-y-5">
            <div className="flex justify-center">
              <span className={`px-4 py-1.5 rounded-full text-xs font-medium border ${statusColors[currentStatus] || statusColors.pending}`}>
                {statusLabels[currentStatus] || currentStatus}
              </span>
            </div>

            <div className="bg-gradient-to-r from-[rgba(212,175,55,0.1)] to-transparent rounded-sm p-4 border border-[rgba(212,175,55,0.15)]">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[rgba(212,175,55,0.15)] flex items-center justify-center">
                  <Scissors className="w-5 h-5 gold-text" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Assigned Worker</p>
                  <p className="text-lg font-semibold text-foreground font-serif">{data.worker_name}</p>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Package className="w-3 h-3" /> Articles
                </p>
                <span className="text-sm font-semibold gold-text">{data.article_count} items</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {data.articles.map((article, idx) => {
                  const isHighlighted = articleNum && String(idx + 1) === String(articleNum);
                  return (
                    <div
                      key={idx}
                      className={`bg-background rounded-sm px-3 py-2.5 border flex items-center gap-2 transition-colors ${
                        isHighlighted ? "border-[#D4AF37] bg-[rgba(212,175,55,0.08)]" : "border-border"
                      }`}
                    >
                      <Shirt className={`w-4 h-4 ${isHighlighted ? "gold-text" : "text-[#722F37]"}`} />
                      <span className="text-sm font-medium text-foreground capitalize">
                        {typeLabels[article] || article.replace(/_/g, " ")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="text-center pt-2 border-t border-border">
              <p className="text-[10px] text-muted-foreground/70 flex items-center justify-center gap-1">
                <Clock className="w-3 h-3" />
                Scanned: {new Date(data.scanned_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-[9px] text-muted-foreground/40 text-center uppercase tracking-widest">
          Admin login required to update status
        </p>
      </div>
    </div>
  );
}
